import { io } from 'socket.io-client';
import { useIsAuthenticated } from '@interhyp/platform-auth';
import { getAPM } from '@interhyp/platform-apm';
import { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';

const _socketConnection = {
  inst: null
};
function initSocketConnection(path = `/api/frontend-notification/socket.io`) {
  if (_socketConnection.inst === null) {
    _socketConnection.inst = io({
      path,
      reconnectionAttempts: 3,
      randomizationFactor: 0.7,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 8000,
      retries: 3
    });
  }
}

function getSocketConnection() {
  if (_socketConnection.inst === null) {
    throw new Error('socket not initialized.');
  }
  return _socketConnection.inst;
}

function registerForAll(cb) {
  const socket = getSocketConnection();
  socket.onAny(cb);
  return () => {
    socket.offAny(cb);
  };
}

function registerAtNamespace(namespace, cb) {
  const socket = getSocketConnection();
  socket.on(`ns-${namespace}`, cb);
  return () => {
    socket.off(`ns-${namespace}`, cb);
  };
}

const NotificationContext = /*#__PURE__*/createContext({
  status: undefined
});

function destroySocketConnection() {
  try {
    const socket = getSocketConnection();
    socket.disconnect();
    _socketConnection.inst = null;
  } catch {}
}

/**
 * This is the raw notification provider which just initializes the socket connection.
 * The auth check has to be done outside this provider
 * @param children
 * @param shouldInit
 * @constructor
 */
function NotificationInitProvider({
  children,
  shouldInit
}) {
  const [connectionStatus, setConnectionStatus] = useState();
  const [retryCounter, setRetryCounter] = useState(0);

  // Init authentication
  useEffect(() => {
    if (!shouldInit && !_socketConnection.inst) {
      return;
    }
    initSocketConnection();
    setConnectionStatus('connecting');
    const onError = e => {
      destroySocketConnection();
      setConnectionStatus('error');
      // code attr should be on error object regarding the documentation https://socket.io/docs/v4/troubleshooting-connection-issues/
      getAPM().captureError(`Notification socket error - Code: ${e?.code} - Message: ${e?.message}`);
    };
    const onSuccess = () => {
      setConnectionStatus('connected');
    };
    _socketConnection.inst?.on('connect_error', onError);
    _socketConnection.inst?.on('connect', onSuccess);
    return () => {
      destroySocketConnection();
      setConnectionStatus(void 0);
      _socketConnection.inst?.off('connect_error', onError);
      _socketConnection.inst?.off('connect', onSuccess);
    };
  }, [shouldInit, retryCounter]);

  // Re-init on error
  // this is required when the user session has been expired while surfing on a different tab
  const onWindowFocus = useCallback(() => {
    if (connectionStatus === 'error') {
      setRetryCounter(c => c + 1);
    }
  }, [connectionStatus]);
  useEffect(() => {
    window?.addEventListener('focus', onWindowFocus);
    return () => {
      window?.removeEventListener('focus', onWindowFocus);
    };
  }, [onWindowFocus]);
  const contextValue = useMemo(() => ({
    status: connectionStatus
  }), [connectionStatus]);
  return /*#__PURE__*/jsx(NotificationContext.Provider, {
    value: contextValue,
    children: children
  });
}

/**
 * This provider inits the socket connection if the user is authenticated
 * @param children
 * @constructor
 */
function NotificationProvider({
  children
}) {
  const isAuthenticated = useIsAuthenticated();
  return /*#__PURE__*/jsx(NotificationInitProvider, {
    shouldInit: isAuthenticated,
    children: children
  });
}

function useSocketConnectionStatus() {
  const {
    status
  } = useContext(NotificationContext);
  return status;
}

/**
 * This hook register at a namespace as soon as the socket connection is established
 * @param namespace
 * @param callback - use useCallback for the callback function, otherwise it will re-register on every render step
 * @return status
 */
function useRegisterAtNamespace(namespace, callback) {
  const status = useSocketConnectionStatus();
  useEffect(() => {
    if (status !== 'connected') {
      return;
    }
    return registerAtNamespace(namespace, callback);
  }, [namespace, callback, status]);
  return status;
}

/**
 * This hook register for all as soon as the socket connection is established
 * @param callback - use useCallback for the callback function, otherwise it will re-register on every render step
 * @return status
 */
function useRegisterForAll(callback) {
  const status = useSocketConnectionStatus();
  useEffect(() => {
    if (status !== 'connected') {
      return;
    }
    return registerForAll(callback);
  }, [callback, status]);
  return status;
}

export { NotificationInitProvider, NotificationProvider, _socketConnection, destroySocketConnection, getSocketConnection, initSocketConnection, registerAtNamespace, registerForAll, useRegisterAtNamespace, useRegisterForAll, useSocketConnectionStatus };
//# sourceMappingURL=index.js.map
